import React, { useState, useEffect } from 'react';
import { Customer, CustomerType, CustomerPerson, Tag } from '../types';
import { customerPersonsAPI } from '../services/api';

interface CustomersTableProps {
  customers: Customer[];
  tags: Tag[];
  typeFilter: number;
  isArchiveFilter: number;
  tagFilter: number[];
  searchText: string;
  onSearchTextChange: (newValue: string) => void;
  onTypeFilterChange: (newValue: number) => void;
  onIsArchiveFilterChange: (newValue: number) => void;
  onTagFilterChange: (newTagFilter: number[]) => void;
  onEdit: (customer: Customer) => void;
  onDelete: (id: number) => void;
  onViewDetails: (id: number) => void;
}

const CustomersTable: React.FC<CustomersTableProps> = ({
  customers,
  tags,
  typeFilter,
  isArchiveFilter,
  tagFilter,
  searchText,
  onSearchTextChange,
  onTypeFilterChange,
  onIsArchiveFilterChange,
  onTagFilterChange,
  onEdit,
  onDelete,
  onViewDetails
}) => {
  const [customerPersonsMap, setCustomerPersonsMap] = useState<Record<number, CustomerPerson[]>>({});
  useEffect(() => {
    loadCustomerPersons();
  }, [customers]);

  const loadCustomerPersons = async () => {
    try {
      const allCustomerPersons = await customerPersonsAPI.getCustomerPersons();

      // Group by customerId
      const map: Record<number, CustomerPerson[]> = {};
      allCustomerPersons.forEach(cp => {
        if (!map[cp.customerId]) {
          map[cp.customerId] = [];
        }
        map[cp.customerId].push(cp);
      });

      setCustomerPersonsMap(map);
    } catch (error) {
      console.error('Error loading customer persons:', error);
    }
  };

  const getTypeText = (type: CustomerType) => {
    switch (type) {
      case CustomerType.Government: return 'Government';
      case CustomerType.Education: return 'Education';
      case CustomerType.Legal: return 'Legal';
      case CustomerType.Healthcare: return 'Healthcare';
      case CustomerType.Retail: return 'Retail';
      case CustomerType.Banking: return 'Banking';
      case CustomerType.Nonprofit: return 'Nonprofit';
      case CustomerType.Publishers: return 'Publishers';
      case CustomerType.Agriculture: return 'Agriculture';
      case CustomerType.Logistics: return 'Logistics';
      case CustomerType.Telecommunications: return 'Telecommunications';
      case CustomerType.Media: return 'Media';
      case CustomerType.Hospitality: return 'Hospitality';
      case CustomerType.ITCluster: return 'IT Cluster';
      default: return 'Unknown';
    }
  };

  const getTypeBadgeColor = () => '#4b5563';

  const tagBadgeColor = '#6b7280';

  return (
    <div>
      <div className="table-responsive">
        <table className="table table-striped">
        <thead>
          <tr>
            <th className="text-start">Title</th>
            <th className="text-start">Type</th>
            <th className="text-start">Tags</th>
            <th className="text-start">Persons</th>
            <th className="text-start">Projects</th>
            <th className="text-start">Actions</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => (
            <tr
              key={customer.id}
              style={{
                opacity: customer.isArchive ? 0.45 : 1,
                backgroundColor: customer.isArchive ? '#f2f2f2' : undefined
              }}
            >
              <td>
                <div>
                  <div>{customer.title}</div>
                  {customer.group && (
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      {customer.group}
                    </div>
                  )}
                </div>
              </td>
              <td>
                {typeFilter === -1 ? null : (
                  <span
                    className="badge contact-type-badge"
                    style={{ backgroundColor: getTypeBadgeColor(), color: '#fff' }}
                  >
                    {getTypeText(customer.type)}
                  </span>
                )}
              </td>
              <td>
                {customer.tags && customer.tags.length > 0
                  ? (
                    <div className="d-flex flex-wrap gap-1">
                      {customer.tags.map(tag => (
                        <span
                          key={tag.id}
                          className="badge contact-type-badge"
                          style={{ backgroundColor: tagBadgeColor, color: '#fff' }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )
                  : <span className="text-muted">No tags</span>
                }
              </td>
              <td>
                {customerPersonsMap[customer.id] && customerPersonsMap[customer.id].length > 0
                  ? customerPersonsMap[customer.id].length
                  : 0
                }
              </td>
              <td>{customer.projects.length}</td>
              <td>
                <div className="btn-group action-group" role="group">
                  <button
                    className="btn btn-sm action-btn action-view"
                    onClick={() => onViewDetails(customer.id)}
                    title="View Details"
                  >
                    <i className="fa-solid fa-eye" aria-hidden="true" />
                  </button>
                  <button
                    className="btn btn-sm action-btn action-edit"
                    onClick={() => onEdit(customer)}
                    title="Edit"
                  >
                    <i className="fa-solid fa-pen" aria-hidden="true" />
                  </button>
                  <button
                    className="btn btn-sm action-btn action-delete"
                    onClick={() => onDelete(customer.id)}
                    title="Delete"
                  >
                    <i className="fa-solid fa-trash" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomersTable;
